/**
 * AI-generated budget narrative + assumptions.
 */
import React, { useState } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sparkles, Loader2, FileText } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function BudgetNarrative({ calculation }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');

  const generate = async () => {
    if (!calculation) { toast.error('Run a calculation first'); return; }
    try {
      setLoading(true);
      const res = await ConstructionAPI.generateNarrative(calculation, notes);
      if (res?.ok === false) {
        toast.error(res.error || 'Narrative generation failed');
        return;
      }
      setData(res);
      toast.success('Budget narrative generated');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Narrative generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="budget-narrative">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base inline-flex items-center gap-1.5">
            <FileText className="h-4 w-4 text-purple-600" />Budget narrative &amp; assumptions
          </CardTitle>
          <Button size="sm" onClick={generate} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="narrative-generate-btn">
            {loading ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Generating…</> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" />Generate (AI)</>}
          </Button>
        </div>
        <p className="text-xs text-slate-500">Auto-written executive summary, key drivers, watch-outs and assumptions, powered by Claude Sonnet 4.5.</p>
      </CardHeader>
      <CardContent>
        <Textarea
          placeholder="Optional context for the AI (e.g. 'project funded 50% by USAID, design-build delivery, 18-month constraint')…"
          rows={2}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          className="text-sm mb-3"
          data-testid="narrative-notes"
        />
        {!data && !loading && (
          <div className="text-sm text-slate-500 p-4 bg-slate-50 border border-slate-200 rounded-lg">
            Click <strong>Generate (AI)</strong> to produce an executive narrative + assumptions list from your current calculation.
          </div>
        )}
        {data && (
          <div className="space-y-3">
            <Section title="Executive summary" body={data.executive_summary} />
            <Section title="Key cost drivers" body={data.key_drivers} />
            <Section title="Watch-outs" body={data.watch_outs} />
            {data.assumptions?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Assumptions</h4>
                <ul className="space-y-1.5" data-testid="narrative-assumptions">
                  {data.assumptions.map((a, i) => (
                    <li key={i} className="text-sm text-slate-700 flex gap-2">
                      <span className="text-purple-500">●</span><span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-[11px] text-slate-400">Generated {data.generated_at ? new Date(data.generated_at).toLocaleString() : ''} · Model: {data.model || 'Claude Sonnet 4.5'}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Section({ title, body }) {
  if (!body) return null;
  return (
    <div>
      <h4 className="text-sm font-semibold text-slate-800 mb-1">{title}</h4>
      <p className="text-sm text-slate-700 leading-relaxed">{body}</p>
    </div>
  );
}
